import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

const RECIPIENT = "vendite@londamilano.it";

// WordPress origin — production CMS by default.
// Override with REACT_APP_API_ORIGIN env var if needed (e.g. for local development).
// An explicit empty value ("") means "same origin" — useful with the dev-server
// proxy (see "proxy" in package.json) to reach a local WP without CORS.
const API_ORIGIN = process.env.REACT_APP_API_ORIGIN !== undefined
  ? process.env.REACT_APP_API_ORIGIN
  : "https://cms.londamilano.com";

// Use the `?rest_route=` form which works regardless of WP permalink settings.
const REGISTRATIONS_URL = `${API_ORIGIN}/?rest_route=/wp/v2/registrations`;

const todayISO = () => new Date().toISOString().slice(0, 10);

const CSS = `
  .reg-band {
    background: #3C86A7; color: white; text-transform: uppercase; letter-spacing: .08em;
    font-weight: 600; font-size: 0.9rem; padding: 0.65rem 1.1rem; border-radius: 0.55rem 0.55rem 0 0;
  }
  .reg-label { display:block; font-size:.75rem; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#4b5563; margin-bottom:.35rem; }
  .reg-label .req { color:#dc2626; margin-left:2px; }
  .reg-input { width:100%; padding:.65rem .85rem; background:#fff; border:1px solid #d1d5db; border-radius:.5rem; font-size:.9rem; color:#111827; transition:border-color .15s ease, box-shadow .15s ease, background .15s ease; }
  .reg-input::placeholder { color:#9ca3af; }
  .reg-input:focus { outline:none; border-color:#3C86A7; box-shadow:0 0 0 3px rgba(60,134,167,.18); }
  .reg-input:disabled { background:#f3f4f6; color:#9ca3af; cursor:not-allowed; }
  .reg-input.is-invalid { border-color:#dc2626; box-shadow:0 0 0 3px rgba(220,38,38,.12); }
  .reg-error { margin-top:.35rem; font-size:.75rem; color:#dc2626; }
  .reg-input.is-warn { border-color:#d97706; box-shadow:0 0 0 3px rgba(217,119,6,.14); }
  .reg-warn { margin-top:.35rem; font-size:.75rem; color:#b45309; }
  .reg-check { display:inline-flex; align-items:flex-start; gap:.6rem; cursor:pointer; }
  .reg-check input { appearance:none; width:18px; height:18px; border-radius:4px; border:1.5px solid #9ca3af; background:white; flex-shrink:0; margin-top:2px; transition:all .15s ease; cursor:pointer; }
  .reg-check input:checked { background:#3C86A7; border-color:#3C86A7; background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='white'><path d='M13.485 4.515a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L6.778 9.808l5.293-5.293a1 1 0 011.414 0z'/></svg>"); background-size:14px 14px; background-position:center; background-repeat:no-repeat; }
  .reg-check input:focus-visible { outline:2px solid #3C86A7; outline-offset:2px; }
  .reg-toast { position:fixed; left:50%; bottom:24px; transform:translateX(-50%) translateY(20px); background:#111827; color:white; padding:.75rem 1.1rem; border-radius:.6rem; font-size:.85rem; box-shadow:0 10px 25px rgba(0,0,0,.18); opacity:0; transition:opacity .25s ease, transform .25s ease; z-index:60; pointer-events:none; }
  .reg-toast.show { opacity:1; transform:translateX(-50%) translateY(0); }
  .reg-toast.error { background:#b91c1c; }
  .reg-toast.success { background:#047857; }
  @media (max-width:640px) {
    .reg-sticky-actions { position:sticky; bottom:0; left:0; right:0; background:white; border-top:1px solid #e5e7eb; padding:.75rem 1rem; margin:0 -1rem -1rem; box-shadow:0 -4px 12px rgba(0,0,0,.04); z-index:30; }
  }
`;

const SDI_RE   = /^[A-Z0-9]{6,8}$/;
const TEL_RE   = /^[+0-9\s\-()]{6,}$/;
const CAP_RE   = /^[0-9]{5}$/;
const PROV_RE  = /^[A-Z]{2}$/;
const BIC_RE   = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
const IBAN_RE  = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{23}$/; // 27 alphanumeric chars (IT IBAN length)
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE   = /^https?:\/\/.+/i;

// --- Algorithmic validators (real checksum / control character) --------------
// These verify the *math* of the code, not just its shape, so typos and
// invented numbers are rejected. All three are deterministic — no API needed.

// Partita IVA: 11 digits + Luhn-style check digit (total must be a multiple of 10).
function isValidPartitaIva(value) {
  const v = (value || "").replace(/^IT/i, "").replace(/\s+/g, "");
  if (!/^[0-9]{11}$/.test(v) || v === "00000000000") return false;
  let sum = 0;
  for (let i = 0; i < 11; i++) {
    let n = v.charCodeAt(i) - 48;
    if (i % 2 === 1) { n *= 2; if (n > 9) n -= 9; } // even position (1-indexed)
    sum += n;
  }
  return sum % 10 === 0;
}

// Codice fiscale control-character tables (persona fisica, 16 chars).
const CF_ODD = { '0':1,'1':0,'2':5,'3':7,'4':9,'5':13,'6':15,'7':17,'8':19,'9':21,'A':1,'B':0,'C':5,'D':7,'E':9,'F':13,'G':15,'H':17,'I':19,'J':21,'K':2,'L':4,'M':18,'N':20,'O':11,'P':3,'Q':6,'R':8,'S':12,'T':14,'U':16,'V':10,'W':22,'X':25,'Y':24,'Z':23 };
const CF_EVEN = { '0':0,'1':1,'2':2,'3':3,'4':4,'5':5,'6':6,'7':7,'8':8,'9':9,'A':0,'B':1,'C':2,'D':3,'E':4,'F':5,'G':6,'H':7,'I':8,'J':9,'K':10,'L':11,'M':12,'N':13,'O':14,'P':15,'Q':16,'R':17,'S':18,'T':19,'U':20,'V':21,'W':22,'X':23,'Y':24,'Z':25 };
// Structure tolerant of omocodia (digits replaced by letters L,M,N,P,Q,R,S,T,U,V).
const CF16_RE = /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/;

function isValidCodiceFiscale(value) {
  const v = (value || "").replace(/\s+/g, "").toUpperCase();
  // Persona giuridica: the 11-digit CF coincides with the Partita IVA.
  if (/^[0-9]{11}$/.test(v)) return isValidPartitaIva(v);
  // Persona fisica: 16 chars + control character (last char) over the first 15.
  if (!CF16_RE.test(v)) return false;
  let sum = 0;
  for (let i = 0; i < 15; i++) sum += (i % 2 === 0 ? CF_ODD : CF_EVEN)[v[i]];
  return v[15] === String.fromCharCode(65 + (sum % 26));
}

// IBAN: ISO 7064 mod-97 checksum (remainder over the rearranged string must be 1).
function isValidIban(value) {
  const v = (value || "").replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/.test(v)) return false;
  const rearranged = v.slice(4) + v.slice(0, 4); // move country+check digits to the end
  let remainder = 0;
  for (let i = 0; i < rearranged.length; i++) {
    const c = rearranged.charCodeAt(i);
    const val = c >= 65 ? c - 55 : c - 48; // A-Z -> 10..35, 0-9 -> 0..9
    remainder = (remainder * (val > 9 ? 100 : 10) + val) % 97;
  }
  return remainder === 1;
}

export default function RegistrazioneAnagrafica() {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      valuta: "EUR",
      data_compilazione: todayISO(),
      same_as_legal: false,
      consenso: false,
    },
  });

  const [toast, setToast] = useState({ show: false, kind: "success", text: "" });
  const toastTimer = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const showToast = (text, kind = "success") => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ show: true, kind, text });
    toastTimer.current = setTimeout(() => setToast(t => ({ ...t, show: false })), 3500);
  };

  // Mirror legal -> operativa when "same_as_legal" is on
  const sameAsLegal = watch("same_as_legal");
  const legaleValues = watch(["legale_indirizzo", "legale_cap", "legale_citta", "legale_prov"]);
  useEffect(() => {
    if (!sameAsLegal) return;
    const [ind, cap, citta, prov] = legaleValues;
    setValue("operativa_indirizzo", ind || "");
    setValue("operativa_cap", cap || "");
    setValue("operativa_citta", citta || "");
    setValue("operativa_prov", prov || "");
  }, [sameAsLegal, legaleValues, setValue]);

  // Helpers to build onChange transforms
  const upper = (name) => (e) => setValue(name, (e.target.value || "").toUpperCase(), { shouldValidate: true });
  const lower = (name) => (e) => setValue(name, (e.target.value || "").toLowerCase().trim(), { shouldValidate: true });
  const ibanFmt = (e) => {
    const raw = (e.target.value || "").replace(/\s+/g, "").toUpperCase();
    setValue("iban", raw.replace(/(.{4})/g, "$1 ").trim(), { shouldValidate: true });
  };

  const fieldClass = (key) => "reg-input" + (errors[key] ? " is-invalid" : "");

  // IBAN is optional (e.g. clients paying in advance / B.B.): empty submits fine.
  // If filled, we surface a NON-blocking warning — it never prevents submission.
  const ibanValue = watch("iban");
  const ibanWarning = (() => {
    const stripped = (ibanValue || "").replace(/\s+/g, "").toUpperCase();
    if (!stripped) return null;
    if (stripped.length !== 27) return "L'IBAN italiano deve essere di 27 caratteri.";
    if (!IBAN_RE.test(stripped)) return "Formato IBAN non valido.";
    if (!isValidIban(stripped)) return "IBAN non valido: cifre di controllo errate.";
    return null;
  })();

  const onSubmit = async (data) => {
    if (data.same_as_legal) {
      data.operativa_indirizzo = data.legale_indirizzo;
      data.operativa_cap = data.legale_cap;
      data.operativa_citta = data.legale_citta;
      data.operativa_prov = data.legale_prov;
    }

    // Strip spaces from IBAN before sending
    if (data.iban) data.iban = data.iban.replace(/\s+/g, "");

    setSubmitting(true);
    try {
      const res = await fetch(REGISTRATIONS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, language: "it", website_hp: "" }),
      });
      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        const msg = json.message || `Errore ${res.status}: invio non riuscito.`;
        showToast(msg, "error");
        setSubmitting(false);
        return;
      }

      setSubmitted(true);
      showToast("Registrazione inviata correttamente. Ti contatteremo a breve.", "success");
      setSubmitting(false);
      // Scroll to top so the user sees the confirmation banner
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Registration submit failed:", err);
      showToast("Errore di rete. Riprova più tardi.", "error");
      setSubmitting(false);
    }
  };

  const onInvalid = () => {
    showToast("Controlla i campi evidenziati.", "error");
    const firstErr = Object.keys(errors)[0];
    if (firstErr) {
      const el = document.getElementById(firstErr);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <div className="min-h-screen bg-solitude-100 text-bf-100 font-sans">
      <style dangerouslySetInnerHTML={{ __html: CSS }} />

      {/* Brand strip */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between gap-4">
          <a href="https://londamilano.it" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group">
            <svg className="h-9 sm:h-10 w-auto text-main-100 transition group-hover:opacity-90" viewBox="0 0 861 216" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-label="Londa">
              <path d="M339.695 54.3504C296.311 -18.1888 172.135 -18.0812 128.589 54.3504C99.9539 99.0148 111.204 166.819 154.426 198.46C160.454 187.16 170.035 179.895 180.962 173.168C126.382 127.374 162.069 27.767 234.788 30.4038C293.135 28.8971 333.72 95.786 307.561 147.607C284.308 87.6065 194.311 83.463 194.311 139.697C199.37 127.266 214.603 125.06 225.045 130.441C241.247 139.051 236.995 157.67 223.485 166.657C205.991 178.281 175.956 188.074 169.282 208.685C190.059 197.223 222.085 189.635 256.964 189.043C278.01 188.666 297.603 190.819 317.411 195.608C357.834 163.267 367.415 97.6157 339.695 54.4042" />
              <path d="M378.773 208.093L412.199 214.927V110.369L484.864 215.25H512.854V68.9338L479.428 58.8171V139.428L406.763 36.4849L378.773 28.0363V208.093Z" />
              <path d="M652.639 166.227C648.656 173.115 643.112 178.388 635.953 182.101C628.795 185.815 620.344 187.644 610.548 187.644H579.813V96.5934H610.548C620.29 96.5934 628.741 98.423 635.953 102.136C643.112 105.849 648.71 111.069 652.639 117.903C656.623 124.737 658.614 132.809 658.614 142.119C658.614 151.428 656.623 159.339 652.639 166.227ZM654.2 78.0819C642.09 72.0011 628.095 68.9876 612.216 68.9876H545.795V215.196H612.216C628.095 215.196 642.09 212.183 654.2 206.102C666.311 200.075 675.785 191.572 682.62 180.649C689.456 169.725 692.847 156.863 692.847 142.119C692.847 127.374 689.403 114.513 682.62 103.589C675.785 92.6651 666.311 84.1627 654.2 78.1357" />
              <path d="M755.393 158.209L778.592 102.459L801.791 158.209H755.447H755.393ZM825.475 215.196H861L795.386 68.9876H761.96L696.992 215.196H731.656L744.682 183.877H812.395L825.421 215.196H825.475Z" />
              <path d="M0 68.7186V214.927H131.604L105.284 187.375H34.0718V53.9201L0 68.7186Z" />
            </svg>
            <span className="hidden sm:flex flex-col leading-tight border-l border-gl-200 pl-3 ml-1">
              <span className="text-[11px] uppercase tracking-widest text-dc-100 font-semibold">londamilano</span>
              <span className="text-[11px] text-dc-100">Via Cagliari snc · Trezzano Rosa (MI)</span>
            </span>
          </a>
          <div className="text-right">
            <div className="text-[12px] text-gray-500"><span className="text-main-100 font-semibold">P.IVA</span> 11735470962</div>
            <div className="text-[12px] text-gray-500"><span className="text-main-100 font-semibold">SDI</span> M5UXCR1</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">

        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-block w-16 h-1 bg-main-100 rounded-full mb-4" />
          <h1 className="text-2xl sm:text-3xl font-bold text-bf-100 mb-2">Modulo Registrazione Anagrafica</h1>
          <p className="text-sm text-dc-100 max-w-xl mx-auto">
            Compila tutti i campi richiesti. I dati saranno utilizzati esclusivamente per l'apertura della tua scheda cliente presso londamilano.
          </p>
        </div>

        {submitted && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
            <h2 className="reg-band">Registrazione inviata</h2>
            <div className="p-6 sm:p-8 text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-9 h-9 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </div>
              <h3 className="text-lg font-bold text-bf-100 mb-2">Grazie! Abbiamo ricevuto la tua registrazione.</h3>
              <p className="text-sm text-dc-100 max-w-md mx-auto">
                Il nostro team la verificherà al più presto e ti contatterà all'indirizzo email indicato. Per qualsiasi necessità immediata puoi scrivere a <a href={`mailto:${RECIPIENT}`} className="text-main-100 underline">{RECIPIENT}</a>.
              </p>
            </div>
          </div>
        )}

        <form noValidate onSubmit={handleSubmit(onSubmit, onInvalid)} className={"space-y-6 " + (submitted ? "hidden" : "")}>

          {/* DATI AZIENDALI */}
          <section className="bg-white rounded-xl shadow-sm overflow-hidden">
            <h2 className="reg-band">Dati aziendali</h2>
            <div className="p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="reg-label" htmlFor="ragione_sociale">Ragione sociale <span className="req">*</span></label>
                <input id="ragione_sociale" type="text" className={fieldClass("ragione_sociale")}
                  {...register("ragione_sociale", { required: "Campo obbligatorio." })} />
                {errors.ragione_sociale && <p className="reg-error">{errors.ragione_sociale.message}</p>}
              </div>
              <div>
                <label className="reg-label" htmlFor="partita_iva">Partita IVA <span className="req">*</span></label>
                <input id="partita_iva" type="text" className={fieldClass("partita_iva")}
                  {...register("partita_iva", {
                    required: "Campo obbligatorio.",
                    validate: v => isValidPartitaIva(v) || "Partita IVA non valida (11 cifre, controllo non superato).",
                    onChange: upper("partita_iva"),
                  })} />
                {errors.partita_iva && <p className="reg-error">{errors.partita_iva.message}</p>}
              </div>
              <div>
                <label className="reg-label" htmlFor="codice_fiscale">Codice fiscale <span className="req">*</span></label>
                <input id="codice_fiscale" type="text" className={fieldClass("codice_fiscale")}
                  {...register("codice_fiscale", {
                    required: "Campo obbligatorio.",
                    validate: v => isValidCodiceFiscale(v) || "Codice fiscale non valido (controllo non superato).",
                    onChange: upper("codice_fiscale"),
                  })} />
                {errors.codice_fiscale && <p className="reg-error">{errors.codice_fiscale.message}</p>}
              </div>
              <div>
                <label className="reg-label" htmlFor="codice_sdi">Codice SDI <span className="req">*</span></label>
                <input id="codice_sdi" type="text" placeholder="es. M5UXCR1" className={fieldClass("codice_sdi")}
                  {...register("codice_sdi", {
                    required: "Campo obbligatorio.",
                    pattern: { value: SDI_RE, message: "6-8 caratteri alfanumerici." },
                    onChange: upper("codice_sdi"),
                  })} />
                {errors.codice_sdi && <p className="reg-error">{errors.codice_sdi.message}</p>}
              </div>
              <div>
                <label className="reg-label" htmlFor="pec">PEC</label>
                <input id="pec" type="email" className={fieldClass("pec")}
                  {...register("pec", {
                    pattern: { value: EMAIL_RE, message: "Indirizzo PEC non valido." },
                    onChange: lower("pec"),
                  })} />
                {errors.pec && <p className="reg-error">{errors.pec.message}</p>}
              </div>
              <div>
                <label className="reg-label" htmlFor="sito_web">Sito Web</label>
                <input id="sito_web" type="url" placeholder="https://" className={fieldClass("sito_web")}
                  {...register("sito_web", {
                    pattern: { value: URL_RE, message: "Inserisci un URL valido (https://…)." },
                  })} />
                {errors.sito_web && <p className="reg-error">{errors.sito_web.message}</p>}
              </div>
              <div>
                <label className="reg-label" htmlFor="telefono_aziendale">Telefono <span className="req">*</span></label>
                <input id="telefono_aziendale" type="tel" className={fieldClass("telefono_aziendale")}
                  {...register("telefono_aziendale", {
                    required: "Campo obbligatorio.",
                    pattern: { value: TEL_RE, message: "Numero di telefono non valido." },
                  })} />
                {errors.telefono_aziendale && <p className="reg-error">{errors.telefono_aziendale.message}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="reg-label" htmlFor="email_ordini">E-mail (ricezione ordini) <span className="req">*</span></label>
                <input id="email_ordini" type="email" className={fieldClass("email_ordini")}
                  {...register("email_ordini", {
                    required: "Campo obbligatorio.",
                    pattern: { value: EMAIL_RE, message: "Indirizzo email non valido." },
                    onChange: lower("email_ordini"),
                  })} />
                {errors.email_ordini && <p className="reg-error">{errors.email_ordini.message}</p>}
              </div>
            </div>
          </section>

          {/* SEDE LEGALE */}
          <section className="bg-white rounded-xl shadow-sm overflow-hidden">
            <h2 className="reg-band">Sede legale</h2>
            <div className="p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-6 gap-4">
              <div className="sm:col-span-4">
                <label className="reg-label" htmlFor="legale_indirizzo">Indirizzo <span className="req">*</span></label>
                <input id="legale_indirizzo" type="text" className={fieldClass("legale_indirizzo")}
                  {...register("legale_indirizzo", { required: "Campo obbligatorio." })} />
                {errors.legale_indirizzo && <p className="reg-error">{errors.legale_indirizzo.message}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="reg-label" htmlFor="legale_cap">CAP <span className="req">*</span></label>
                <input id="legale_cap" type="text" inputMode="numeric" maxLength={5} className={fieldClass("legale_cap")}
                  {...register("legale_cap", { required: "Campo obbligatorio.", pattern: { value: CAP_RE, message: "5 cifre." } })} />
                {errors.legale_cap && <p className="reg-error">{errors.legale_cap.message}</p>}
              </div>
              <div className="sm:col-span-4">
                <label className="reg-label" htmlFor="legale_citta">Città <span className="req">*</span></label>
                <input id="legale_citta" type="text" className={fieldClass("legale_citta")}
                  {...register("legale_citta", { required: "Campo obbligatorio." })} />
                {errors.legale_citta && <p className="reg-error">{errors.legale_citta.message}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="reg-label" htmlFor="legale_prov">Provincia <span className="req">*</span></label>
                <input id="legale_prov" type="text" maxLength={2} placeholder="MI" className={fieldClass("legale_prov")}
                  {...register("legale_prov", {
                    required: "Campo obbligatorio.",
                    pattern: { value: PROV_RE, message: "Sigla di 2 lettere." },
                    onChange: upper("legale_prov"),
                  })} />
                {errors.legale_prov && <p className="reg-error">{errors.legale_prov.message}</p>}
              </div>
            </div>
          </section>

          {/* SEDE OPERATIVA */}
          <section className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="reg-band flex items-center justify-between flex-wrap gap-2">
              <span>Sede operativa</span>
              <label className="reg-check text-xs normal-case tracking-normal font-medium text-white/95">
                <input type="checkbox" {...register("same_as_legal")} />
                Stessa della sede legale
              </label>
            </div>
            <div className="p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-6 gap-4">
              <div className="sm:col-span-4">
                <label className="reg-label" htmlFor="operativa_indirizzo">Indirizzo {!sameAsLegal && <span className="req">*</span>}</label>
                <input id="operativa_indirizzo" type="text" disabled={sameAsLegal}
                  className={fieldClass("operativa_indirizzo")}
                  {...register("operativa_indirizzo", {
                    validate: v => sameAsLegal || (v && v.trim() !== "") || "Campo obbligatorio.",
                  })} />
                {errors.operativa_indirizzo && <p className="reg-error">{errors.operativa_indirizzo.message}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="reg-label" htmlFor="operativa_cap">CAP {!sameAsLegal && <span className="req">*</span>}</label>
                <input id="operativa_cap" type="text" inputMode="numeric" maxLength={5} disabled={sameAsLegal}
                  className={fieldClass("operativa_cap")}
                  {...register("operativa_cap", {
                    validate: v => {
                      if (sameAsLegal) return true;
                      if (!v || v.trim() === "") return "Campo obbligatorio.";
                      if (!CAP_RE.test(v)) return "5 cifre.";
                      return true;
                    },
                  })} />
                {errors.operativa_cap && <p className="reg-error">{errors.operativa_cap.message}</p>}
              </div>
              <div className="sm:col-span-4">
                <label className="reg-label" htmlFor="operativa_citta">Città {!sameAsLegal && <span className="req">*</span>}</label>
                <input id="operativa_citta" type="text" disabled={sameAsLegal}
                  className={fieldClass("operativa_citta")}
                  {...register("operativa_citta", {
                    validate: v => sameAsLegal || (v && v.trim() !== "") || "Campo obbligatorio.",
                  })} />
                {errors.operativa_citta && <p className="reg-error">{errors.operativa_citta.message}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="reg-label" htmlFor="operativa_prov">Provincia {!sameAsLegal && <span className="req">*</span>}</label>
                <input id="operativa_prov" type="text" maxLength={2} disabled={sameAsLegal}
                  className={fieldClass("operativa_prov")}
                  {...register("operativa_prov", {
                    validate: v => {
                      if (sameAsLegal) return true;
                      if (!v || v.trim() === "") return "Campo obbligatorio.";
                      if (!PROV_RE.test(v.toUpperCase())) return "Sigla di 2 lettere.";
                      return true;
                    },
                    onChange: upper("operativa_prov"),
                  })} />
                {errors.operativa_prov && <p className="reg-error">{errors.operativa_prov.message}</p>}
              </div>
            </div>
          </section>

          {/* REFERENTI AZIENDALI */}
          <section className="bg-white rounded-xl shadow-sm overflow-hidden">
            <h2 className="reg-band">Referenti aziendali</h2>
            <div className="p-5 sm:p-6 space-y-6">

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="reg-label" htmlFor="comm_nome">Responsabile commerciale</label>
                  <input id="comm_nome" type="text" placeholder="Nome e cognome" className={fieldClass("comm_nome")}
                    {...register("comm_nome")} />
                </div>
                <div>
                  <label className="reg-label" htmlFor="comm_email">E-mail</label>
                  <input id="comm_email" type="email" className={fieldClass("comm_email")}
                    {...register("comm_email", {
                      pattern: { value: EMAIL_RE, message: "Email non valida." },
                      onChange: lower("comm_email"),
                    })} />
                  {errors.comm_email && <p className="reg-error">{errors.comm_email.message}</p>}
                </div>
                <div>
                  <label className="reg-label" htmlFor="comm_tel">Telefono</label>
                  <input id="comm_tel" type="tel" className={fieldClass("comm_tel")}
                    {...register("comm_tel", {
                      pattern: { value: TEL_RE, message: "Numero non valido." },
                    })} />
                  {errors.comm_tel && <p className="reg-error">{errors.comm_tel.message}</p>}
                </div>
              </div>

              <hr className="border-gl-200" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="reg-label" htmlFor="amm_nome">Responsabile amministrativo</label>
                  <input id="amm_nome" type="text" placeholder="Nome e cognome" className={fieldClass("amm_nome")}
                    {...register("amm_nome")} />
                </div>
                <div>
                  <label className="reg-label" htmlFor="amm_email">E-mail</label>
                  <input id="amm_email" type="email" className={fieldClass("amm_email")}
                    {...register("amm_email", {
                      pattern: { value: EMAIL_RE, message: "Email non valida." },
                      onChange: lower("amm_email"),
                    })} />
                  {errors.amm_email && <p className="reg-error">{errors.amm_email.message}</p>}
                </div>
                <div>
                  <label className="reg-label" htmlFor="amm_tel">Telefono</label>
                  <input id="amm_tel" type="tel" className={fieldClass("amm_tel")}
                    {...register("amm_tel", {
                      pattern: { value: TEL_RE, message: "Numero non valido." },
                    })} />
                  {errors.amm_tel && <p className="reg-error">{errors.amm_tel.message}</p>}
                </div>
              </div>

              <hr className="border-gl-200" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="reg-label" htmlFor="agente_nome">Agente di riferimento</label>
                  <input id="agente_nome" type="text" placeholder="Nome e cognome" className={fieldClass("agente_nome")}
                    {...register("agente_nome")} />
                </div>
                <div>
                  <label className="reg-label" htmlFor="agente_email">E-mail agente</label>
                  <input id="agente_email" type="email" className={fieldClass("agente_email")}
                    {...register("agente_email", {
                      pattern: { value: EMAIL_RE, message: "Email non valida." },
                      onChange: lower("agente_email"),
                    })} />
                  {errors.agente_email && <p className="reg-error">{errors.agente_email.message}</p>}
                </div>
                <div>
                  <label className="reg-label" htmlFor="agente_tel">Telefono</label>
                  <input id="agente_tel" type="tel" className={fieldClass("agente_tel")}
                    {...register("agente_tel", {
                      pattern: { value: TEL_RE, message: "Numero non valido." },
                    })} />
                  {errors.agente_tel && <p className="reg-error">{errors.agente_tel.message}</p>}
                </div>
              </div>
            </div>
          </section>

          {/* DATI BANCARI */}
          <section className="bg-white rounded-xl shadow-sm overflow-hidden">
            <h2 className="reg-band">Dati bancari</h2>
            <div className="p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="reg-label" htmlFor="banca">Banca</label>
                <input id="banca" type="text" className={fieldClass("banca")} {...register("banca")} />
              </div>
              <div>
                <label className="reg-label" htmlFor="agenzia">Agenzia</label>
                <input id="agenzia" type="text" className={fieldClass("agenzia")} {...register("agenzia")} />
              </div>
              <div>
                <label className="reg-label" htmlFor="iban">IBAN</label>
                <input id="iban" type="text" maxLength={33} placeholder="IT60 X054 2811 1010 0000 0123 456"
                  className={"reg-input font-mono tracking-wide" + (ibanWarning ? " is-warn" : "")}
                  {...register("iban", { onChange: ibanFmt })} />
                {ibanWarning && <p className="reg-warn">⚠ {ibanWarning} Campo facoltativo: puoi inviare comunque.</p>}
              </div>
              <div>
                <label className="reg-label" htmlFor="bic">BIC / SWIFT</label>
                <input id="bic" type="text" placeholder="BCITITMM"
                  className={fieldClass("bic") + " font-mono"}
                  {...register("bic", {
                    pattern: { value: BIC_RE, message: "Formato BIC/SWIFT non valido." },
                    onChange: upper("bic"),
                  })} />
                {errors.bic && <p className="reg-error">{errors.bic.message}</p>}
              </div>
            </div>
          </section>

          {/* CONSENSO */}
          <section className="bg-white rounded-xl shadow-sm overflow-hidden">
            <h2 className="reg-band">Dichiarazione di consenso</h2>
            <div className="p-5 sm:p-6 space-y-4">
              <p className="text-xs leading-relaxed text-dc-100">
                Ai sensi del Regolamento (UE) 2016/679 del Parlamento Europeo e del Consiglio del 27 aprile 2016, relativo alla protezione delle persone fisiche con riguardo al trattamento dei dati personali, e che abroga la direttiva 95/46/CE, è possibile utilizzare gli strumenti del fax e della posta elettronica per l'invio di comunicazioni commerciali (ad es. listini, offerte, conferme d'ordine, fatture, ecc.) solo con il previo consenso del soggetto interessato. In qualsiasi momento l'interessato potrà opporsi all'invio ulteriore di comunicazioni commerciali da parte di londamilano comunicandolo per posta elettronica all'indirizzo{" "}
                <a href={`mailto:${RECIPIENT}`} className="text-main-100 underline">{RECIPIENT}</a>.
              </p>

              <label className="reg-check text-sm">
                <input type="checkbox" {...register("consenso", { required: "Il consenso è obbligatorio per inviare il modulo." })} />
                <span>
                  Acconsento al trattamento dei miei dati personali e all'invio di comunicazioni commerciali da parte di londamilano. <span className="req">*</span>
                </span>
              </label>
              {errors.consenso && <p className="reg-error">{errors.consenso.message}</p>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="reg-label" htmlFor="luogo">Luogo <span className="req">*</span></label>
                  <input id="luogo" type="text" placeholder="es. Milano" className={fieldClass("luogo")}
                    {...register("luogo", { required: "Campo obbligatorio." })} />
                  {errors.luogo && <p className="reg-error">{errors.luogo.message}</p>}
                </div>
                <div>
                  <label className="reg-label" htmlFor="data_compilazione">Data <span className="req">*</span></label>
                  <input id="data_compilazione" type="date" className={fieldClass("data_compilazione")}
                    {...register("data_compilazione", { required: "Campo obbligatorio." })} />
                  {errors.data_compilazione && <p className="reg-error">{errors.data_compilazione.message}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className="reg-label" htmlFor="firmatario">Nome e ruolo del firmatario <span className="req">*</span></label>
                  <input id="firmatario" type="text" placeholder="es. Mario Rossi – Amministratore Unico" className={fieldClass("firmatario")}
                    {...register("firmatario", { required: "Campo obbligatorio." })} />
                  {errors.firmatario && <p className="reg-error">{errors.firmatario.message}</p>}
                </div>
              </div>
            </div>
          </section>

          <div className="reg-sticky-actions flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs text-dc-100 sm:max-w-md">
              I tuoi dati verranno trattati ai sensi del Regolamento (UE) 2016/679 e inviati esclusivamente a londamilano.
            </p>
            <div className="flex gap-2">
              <button type="reset" disabled={submitting}
                className="px-4 py-2.5 rounded-lg border border-gl-200 text-bf-100 text-sm font-medium hover:bg-gl-100 transition disabled:opacity-50 disabled:cursor-not-allowed">
                Annulla
              </button>
              <button type="submit" disabled={submitting}
                className="px-6 py-2.5 rounded-lg bg-main-100 hover:bg-[#2f6c8a] text-white text-sm font-semibold shadow-sm transition flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                {submitting ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" /><path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" className="opacity-75" /></svg>
                    Invio in corso…
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    Invia richiesta
                  </>
                )}
              </button>
            </div>
          </div>
        </form>

        <footer className="text-center mt-10 mb-2">
          <a href="https://londamilano.it" target="_blank" rel="noopener noreferrer" className="text-main-100 font-semibold text-sm hover:underline">londamilano.it</a>
        </footer>
      </main>

      <div className={"reg-toast " + (toast.show ? "show " : "") + toast.kind} role="status" aria-live="polite">
        {toast.text}
      </div>
    </div>
  );
}
